import { useState } from "react";
import { PostcardActionAttributes } from "../Post.types";
import {
    FlexWrapContainer,
    StyledActionButton,
} from "./PostActionButtons.styles";
import { ReactionListPropsType } from "@shared/Components/Tippspiel/Reactions/ReactionsList.types";
import { EntityReactionsListWithDefaultIcons } from "@shared/Components/Tippspiel/Reactions/EntityReactionsListWithDefaultIcons";
import { Box, Typography } from "@mui/material";
import { useInterruptableAction } from "@infrastructure/Persistence/UserState";
import { CommentIcon } from "@icons/CommentIcon";
import { ShareIcon } from "@icons/ShareIcon";
import { getUserReactionOnObject } from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import { ReportIcon } from "@icons/ReportIcon";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { useReactions } from "../../Reactions/Hooks/useReactions";
import { PostReactionActor } from "../../Reactions";
import { CommentSolidIcon } from "@icons/CommentSolidIcon";
import { useUserCommentedPostsStore } from "@infrastructure/Hooks/UserInteractions/useUserCommentedPostsStore";
import { isSharingNativelySupported } from "../../../Pages/TruckTourFeedPage/Helpers/ShareSupported";

export function PostActionButtonsCommentsAndReactions(
    props: PostcardActionAttributes,
) {
    const {
        postId,
        postData,
        user,
        onCommentButtonClick,
        onReportButtonClick,
        onShareButtonClicked,
        showReportOption = true,
        showShareButton = true,
    } = props;
    const commentsCount = postData?.commentsCount ?? 0;

    const { reactOnEntity: reactOnPost } = useReactions(
        postId.toString(),
        postData?.postReactions ?? [],
        PostReactionActor,
    );

    const [isCreatingChat, setIsCreatingChat] = useState(false);

    function createDirectChat() {
        setIsCreatingChat(false);
    }

    const cancelCreateChat = () => {
        setIsCreatingChat(false);
    };

    useInterruptableAction(isCreatingChat, createDirectChat, cancelCreateChat);

    const reactionsListProps: ReactionListPropsType = {
        reactOnEntity: reactOnPost,
        userReaction: getUserReactionOnObject(
            postId.toString(),
            ReactionableObjectEnum.POST,
        ),
        reactionCountSum: props.postData?.reactionCountSum ?? 0,
        reactions: postData?.postReactions ?? [],
    };

    const isOwnComment = useIsCurrentUserId(user.uuid);

    const { hasUserCommentedOnPost } = useUserCommentedPostsStore();
    const isSharingAvailableInApp = isSharingNativelySupported();

    return (
        <FlexWrapContainer>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    color: "var(--community-color-primary-text)",
                    gap: "4px",
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <EntityReactionsListWithDefaultIcons {...reactionsListProps} />
            </Box>
            <StyledActionButton
                disableRipple
                onClick={(e) => {
                    onCommentButtonClick(e, postId);
                }}
            >
                {hasUserCommentedOnPost(postId) ? (
                    <CommentSolidIcon
                        width={"16px"}
                        height={"16px"}
                        fill={"var(--community-color-primary-text)"}
                    />
                ) : (
                    <CommentIcon width={"16px"} height={"16px"} />
                )}
                <Typography fontSize={"14px"}>
                    {commentsCount.toLocaleString("de-DE")}
                </Typography>
            </StyledActionButton>
            {isSharingAvailableInApp && showShareButton && (
                <StyledActionButton
                    disableRipple
                    onClick={(e) => {
                        e.stopPropagation();
                        onShareButtonClicked();
                    }}
                >
                    <ShareIcon width={16} height={16} />
                </StyledActionButton>
            )}
            {!isOwnComment && showReportOption && (
                <StyledActionButton
                    disableRipple
                    onClick={(e) => {
                        e.stopPropagation();
                        onReportButtonClick();
                    }}
                >
                    <ReportIcon width={16} height={16} />
                </StyledActionButton>
            )}
        </FlexWrapContainer>
    );
}
